import { useState, useRef, useEffect } from 'react';

export interface ImageSelection {
  thumbnails: Record<string, string>;
  gallery: string[];
  pdpImages: string[];
}

export const useImageEditor = () => {
  const [imageSelection, setImageSelection] = useState<ImageSelection>({
    thumbnails: {},
    gallery: [],
    pdpImages: [],
  });
  const [deletedDetailedImages, setDeletedDetailedImages] = useState<Set<string>>(new Set());
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const justSavedRef = useRef(false);
  const originalThumbnailsRef = useRef<Record<string, string>>({});
  const originalGalleryRef = useRef<string[]>([]);
  const originalPdpImagesRef = useRef<string[]>([]);

  useEffect(() => {
    if (justSavedRef.current) {
      justSavedRef.current = false;
      return;
    }
    if (isInitialLoad) {
      return;
    }

    const thumbnailsChanged =
      JSON.stringify(imageSelection.thumbnails) !== JSON.stringify(originalThumbnailsRef.current);
    const galleryChanged = JSON.stringify(imageSelection.gallery) !== JSON.stringify(originalGalleryRef.current);
    const pdpChanged = JSON.stringify(imageSelection.pdpImages) !== JSON.stringify(originalPdpImagesRef.current);
    const hasDeletedImages = deletedDetailedImages.size > 0;

    const hasChanges = thumbnailsChanged || galleryChanged || pdpChanged || hasDeletedImages;
    setHasUnsavedChanges(hasChanges);
  }, [imageSelection, deletedDetailedImages, isInitialLoad]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const markAsSaved = () => {
    justSavedRef.current = true;
    setHasUnsavedChanges(false);
    originalThumbnailsRef.current = imageSelection.thumbnails;
    originalGalleryRef.current = imageSelection.gallery;
    originalPdpImagesRef.current = imageSelection.pdpImages;
  };

  return {
    imageSelection,
    setImageSelection,
    deletedDetailedImages,
    setDeletedDetailedImages,
    isInitialLoad,
    setIsInitialLoad,
    hasUnsavedChanges,
    originalThumbnailsRef,
    originalGalleryRef,
    originalPdpImagesRef,
    markAsSaved,
  };
};
